import React from 'react';
import { Crown, X, Check, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CustomVideoPlayer from './CustomVideoPlayer';

interface PostExportUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  clipPreviewUrl?: string;
  hasWatermark: boolean;
}

const PostExportUpsellModal: React.FC<PostExportUpsellModalProps> = ({
  isOpen,
  onClose,
  clipPreviewUrl,
  hasWatermark
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* Header */}
        <div className="relative bg-white px-8 py-6 border-b border-gray-200">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Sparkles className="w-6 h-6 text-gray-700" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">🎉 Your Clip is Ready!</h2>
          </div>
          <p className="text-gray-600">Love the results? Upgrade to unlock the full potential</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Video Preview (if available) */}
          {clipPreviewUrl && (
            <div className="mb-6 rounded-xl overflow-hidden border-2 border-gray-200">
              <div className="relative" style={{ height: '300px' }}>
                <CustomVideoPlayer
                  src={clipPreviewUrl}
                  className="w-full h-full"
                />
              </div>
            </div>
          )}

          {/* Comparison Section */}
          {hasWatermark && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
                See the Difference with Pro
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                 {/* Free Version */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                  <div className="text-center mb-3">
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm font-semibold">
                      Free Version
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <X className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Large "CLIPTHIS" watermark</span>
                    </li>
                    <li className="flex items-start">
                      <X className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">720p quality max</span>
                    </li>
                    <li className="flex items-start">
                      <X className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Limited export formats</span>
                    </li>
                    <li className="flex items-start">
                      <X className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">1 video lifetime</span>
                    </li>
                  </ul>
                </div>

                {/* Pro Version */}
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
                  <div className="text-center mb-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded text-sm font-semibold">
                      <Crown className="w-3 h-3" />
                      Pro Version
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-900 font-medium">No watermarks</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-900 font-medium">Full 1080p quality</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-900 font-medium">All export formats</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-900 font-medium">10-50 videos/month</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Benefits List */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Upgrade to Pro and Get:
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-start">
                <Check className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Remove all watermarks</span>
              </div>
              <div className="flex items-start">
                <Check className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Priority processing (2x faster)</span>
              </div>
              <div className="flex items-start">
                <Check className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">YouTube downloader & more tools</span>
              </div>
              <div className="flex items-start">
                <Check className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Advanced AI analysis</span>
              </div>
            </div>
          </div>

          {/* Special Offer Banner */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="text-3xl">🔥</div>
              <div className="flex-1">
                <div className="font-bold text-gray-900">Limited Time Offer!</div>
                <div className="text-sm text-gray-700">First-time special: Start at just $9.99/month</div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleUpgrade();
              }}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-red-700 hover:to-red-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <Crown className="w-5 h-5" />
              Upgrade to Pro Now
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="sm:w-auto px-6 py-4 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Maybe Later
            </button>
          </div>

          {/* Trust Signals */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <span>✓ Cancel anytime</span>
              <span>✓ Instant access</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostExportUpsellModal;

